import { Suspense, lazy } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router';
import { CONSOLE_LOCALES, DEFAULT_BASE_URLS, getBaseUrl, getLocale, setBaseUrl, setLocale, type ConsoleLocale, type EnvName } from './api';
import { AccountPage } from './pages/Account';
import { ChatPage } from './pages/Chat';
import { ConnectionsPage } from './pages/Connections';
import { ContractPage } from './pages/Contract';
import { CreditsPage } from './pages/Credits';
import { LibraryPage } from './pages/Library';
import { LoginPage } from './pages/Login';
import { MemoryPage } from './pages/Memory';
import { PersonasPage } from './pages/Personas';
import { PresetsPage } from './pages/Presets';
import { SettingsPage } from './pages/Settings';
import { SyncPage } from './pages/Sync';
import { SystemPage } from './pages/System';
import { WorldBooksPage } from './pages/WorldBooks';
import { useSession } from './session';

// The engine page bundles @pockettavern/prompt-engine + zod; load it on demand.
const EnginePage = lazy(() => import('./pages/Engine').then((m) => ({ default: m.EnginePage })));

const ENVS: EnvName[] = ['local', 'staging', 'production'];

function EnvSwitcher() {
  const { env, switchEnv } = useSession();
  return (
    <div className="flex items-center gap-2">
      <select className="input w-auto py-1" value={getLocale()} title="Accept-Language：服务端按此本地化标签 / 分类名（add-tag-taxonomy）" onChange={(e) => { setLocale(e.target.value as ConsoleLocale); location.reload(); }}>
        {CONSOLE_LOCALES.map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
      </select>
      <select className="input w-auto py-1" value={env} onChange={(e) => switchEnv(e.target.value as EnvName)}>
        {ENVS.map((e) => (
          <option key={e} value={e}>
            {e}
          </option>
        ))}
      </select>
      <input
        className="input w-96 py-1 font-mono text-xs"
        defaultValue={getBaseUrl(env)}
        key={env}
        onBlur={(e) => setBaseUrl(env, e.target.value || DEFAULT_BASE_URLS[env])}
        title="Gateway base URL（失焦保存）"
      />
    </div>
  );
}

function Shell() {
  const { me, logout } = useSession();
  const link = ({ isActive }: { isActive: boolean }) =>
    `rounded-lg px-3 py-1.5 text-sm ${isActive ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-zinc-100'}`;
  return (
    <div className="min-h-screen">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 px-6 py-3">
        <div className="flex items-center gap-4">
          <span className="text-base font-semibold tracking-tight">PocketTavern Console</span>
          <nav className="flex gap-1">
            <NavLink to="/system" className={link}>系统</NavLink>
            <NavLink to="/library" className={link}>角色库</NavLink>
            <NavLink to="/world-books" className={link}>世界书</NavLink>
            <NavLink to="/presets" className={link}>预设</NavLink>
            <NavLink to="/personas" className={link}>人设</NavLink>
            <NavLink to="/settings" className={link}>设置</NavLink>
            <NavLink to="/sync" className={link}>同步</NavLink>
            <NavLink to="/connections" className={link}>连接</NavLink>
            <NavLink to="/chat" className={link}>聊天</NavLink>
            <NavLink to="/engine" className={link}>引擎</NavLink>
            <NavLink to="/memory" className={link}>记忆</NavLink>
            <NavLink to="/contract" className={link}>契约</NavLink>
            <NavLink to="/credits" className={link}>钻石</NavLink>
            <NavLink to="/account" className={link}>账号</NavLink>
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <EnvSwitcher />
          {me && (
            <>
              <span className="text-xs text-zinc-400">
                {me.email} · <span className="font-mono">{me.display_code}</span> · {me.vip_tier}
                {me.features.admin_role ? ` · ${String(me.features.admin_role)}` : ''}
              </span>
              <button className="btn-ghost" title="只退出当前设备（change 10）；「登出所有设备」在账号页" onClick={() => void logout(false)}>登出</button>
            </>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-6 py-6">
        <Routes>
          <Route path="/" element={<Navigate to="/system" replace />} />
          <Route path="/system" element={<SystemPage />} />
          <Route path="/library" element={me ? <LibraryPage /> : <LoginPage />} />
          <Route path="/world-books" element={me ? <WorldBooksPage /> : <LoginPage />} />
          <Route path="/presets" element={me ? <PresetsPage /> : <LoginPage />} />
          <Route path="/personas" element={me ? <PersonasPage /> : <LoginPage />} />
          <Route path="/settings" element={me ? <SettingsPage /> : <LoginPage />} />
          <Route path="/sync" element={me ? <SyncPage /> : <LoginPage />} />
          <Route path="/connections" element={me ? <ConnectionsPage /> : <LoginPage />} />
          <Route path="/chat" element={me ? <ChatPage /> : <LoginPage />} />
          <Route path="/engine" element={me ? <Suspense fallback={<div className="text-sm text-zinc-400">加载引擎…</div>}><EnginePage /></Suspense> : <LoginPage />} />
          <Route path="/memory" element={me ? <MemoryPage /> : <LoginPage />} />
          <Route path="/contract" element={<ContractPage />} />
          <Route path="/credits" element={me ? <CreditsPage /> : <LoginPage />} />
          <Route path="/account" element={me ? <AccountPage /> : <LoginPage />} />
          <Route path="*" element={<Navigate to="/system" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export function App() {
  const { loading } = useSession();
  if (loading) return <div className="p-10 text-sm text-zinc-400">加载中…</div>;
  return <Shell />;
}
